import { useMemo, useState } from "react";
import { PlayAudioButton } from "@/components/PlayAudioButton";
import { Button } from "@/components/ui/button";
import { useRomanianAudio } from "@/lib/audio";
import { useT } from "@/lib/i18n";
import type { ScenarioWord } from "@/lib/scenarios";

type WordStepProps = {
  lemmas: readonly string[];
  words: readonly ScenarioWord[];
  onComplete: () => void;
};

/**
 * WORD step — small batch (2–4) of vocabulary cards in flip-card form.
 *
 * Doctrine §8.3.1 calls for reusing `<StudyWordCard>` here. The Study
 * card pulls from the SRS queue (`/reviews/queue`) and is tightly
 * coupled to that flow; lifting it into a freestanding mode would
 * mean re-plumbing audio + image + the SM-2 grade row. For Phase 5
 * we render a leaner inline flip card with just the lemma + RU
 * translation + pronunciation hint — enough for "I see this word
 * for the first time inside this scenario." Open-mode review picks
 * the SRS up later.
 *
 * The component scrolls through the batch one card at a time. Tap
 * "Знаю" to advance; final tap completes the step.
 *
 * Mockup: `design/hifi/scenarios-extra.jsx` `SessionWordStepScreen`
 * lines ~412–470.
 */
export function WordStep({ lemmas, words, onComplete }: WordStepProps) {
  const t = useT();
  const [revealed, setRevealed] = useState(false);
  const [cursor, setCursor] = useState(0);
  // Resolve lemmas → ScenarioWord rows from the detail payload we
  // already have. If a lemma is missing (typo in scenario JSON),
  // render a stub so the user can still advance — surfacing the
  // typo via "—" beats blocking the session.
  const cards = useMemo(() => {
    const byLemma = new Map(words.map((w) => [w.lemma, w]));
    return lemmas.map(
      (lemma) =>
        byLemma.get(lemma) ?? {
          lemma,
          translation: null,
          pos: null,
          gender: null,
          pronunciation: null,
        },
    );
  }, [lemmas, words]);

  if (cards.length === 0) {
    // Defensive — generator shouldn't emit empty WORD steps. If it
    // ever does, advance immediately rather than render dead UI.
    onComplete();
    return null;
  }

  const card = cards[cursor]!;
  const isLast = cursor === cards.length - 1;
  // Autoplay the Romanian lemma when it's visible. The hook fires on
  // string-identity change of its arg, so switching cards triggers a
  // fresh play and the manual-replay button picks up the new word
  // without us threading state.
  const { play } = useRomanianAudio(card.lemma);

  const advance = () => {
    if (isLast) {
      onComplete();
    } else {
      setCursor(cursor + 1);
      setRevealed(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex items-center justify-between font-mono text-[11px] text-text-3">
        <span>
          {cursor + 1} / {cards.length}
        </span>
        {card.pos && (
          <span>
            {card.pos.toLowerCase()}
            {card.gender ? ` · ${card.gender.toLowerCase()}` : ""}
          </span>
        )}
      </div>
      <button
        type="button"
        className="flashcard flex-1 cursor-pointer text-left"
        onClick={() => !revealed && setRevealed(true)}
      >
        <div className="flex flex-col items-center gap-2 py-6">
          <div className="flex items-center gap-3">
            <div className="text-[36px] font-bold leading-[1.1] tracking-[-0.02em] text-text">{card.lemma}</div>
            <PlayAudioButton play={play} />
          </div>
          {card.pronunciation && <div className="font-mono text-[13px] text-text-3">{card.pronunciation}</div>}
        </div>
        {revealed && card.translation && (
          <div className="border-t border-dashed border-border pt-3 text-center text-[18px] text-text-2">
            {card.translation}
          </div>
        )}
        {!revealed && (
          <div className="flex flex-1 items-center justify-center pt-1 text-[13px] text-text-3">
            {t("study.tap_or_space")}
          </div>
        )}
      </button>
      <div className="flex gap-2">
        <Button variant="scenario" size="block" onClick={advance} disabled={!revealed}>
          {isLast ? t("skills.session_continue") : t("skills.session_continue")}
        </Button>
      </div>
    </div>
  );
}
